import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { backdrop, panel } from "../motion";
import { IconX } from "./icons";
import type { ResidentDetail } from "../hooks/useIcpFlow";

// ICP history card — opened from the resident quick view ("ICP History"
// button). Groups the resident's ICP facts (last/next dates, paper-form slot)
// with the full per-resident cycle history (icp_cycles, newest first). Kept
// mounted while the quick view is open so AnimatePresence can play the same
// backdrop/panel exit as the quick view when `open` flips to false.

export function IcpHistoryCard({ detail, open, onClose }: { detail: ResidentDetail; open: boolean; onClose: () => void }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: globalThis.KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open ? (
        <motion.div className="palette-backdrop is-top" role="dialog" aria-modal="true" aria-label="ICP history" onClick={onClose} variants={backdrop} initial="hidden" animate="show" exit="exit">
          <motion.div className="modal" onClick={(e) => e.stopPropagation()} variants={panel} initial="hidden" animate="show" exit="exit">
            <div className="modal-head">
              <h2>ICP history</h2>
              {detail.name ? <span className="chip">{detail.name}</span> : null}
              {detail.bed ? <span className="chip">{detail.bed}</span> : null}
              <button type="button" className="btn btn-icon btn-ghost" aria-label="Close" onClick={onClose}><IconX /></button>
            </div>
            <div className="modal-body">
              <dl className="quickview-facts">
                <div><dt>Last ICP</dt><dd>{detail.lastIcpDate ?? "—"}</dd></div>
                <div><dt>Next ICP</dt><dd>{detail.nextIcpDate ? `${detail.nextIcpDate}${detail.icpFrequency ? ` (${detail.icpFrequency})` : ""}` : "—"}</dd></div>
                <div><dt>Paper form (ES-RH-007i)</dt><dd>{detail.paperSlot ? detail.paperSlot.label : "—"}{detail.addedToPaperForm ? " · on form" : ""}</dd></div>
              </dl>
              <p className="queue-subhead">Completed ICPs</p>
              {detail.icpHistory.length > 0 ? (
                <ul className="icp-history-list">
                  {detail.icpHistory.map((h) => (
                    <li key={h.id}>
                      <span className="chip chip-ok">ICP completed</span>
                      <span className="chip">{h.icpDate}</span>
                      {h.frequencyAfter ? <span className="chip">{h.frequencyAfter}</span> : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted">No completed ICPs yet.</p>
              )}
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
